import { FC } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Avatar } from '../../ui/Avatar';
import { cn } from '../../../utils/cn';

export interface TherapistCardProps {
  /**
   * Unique identifier for the therapist
   */
  id: string;

  /**
   * Therapist's full name
   */
  name: string;

  /**
   * Title or specialty (e.g., "Licensed Massage Therapist", "Deep Tissue Specialist")
   */
  title: string;

  /**
   * Years of professional experience
   */
  yearsExperience: number;

  /**
   * Brief bio (2-3 sentences)
   */
  bio: string;

  /**
   * Profile image URL (optional)
   */
  imageUrl?: string;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export const TherapistCard: FC<TherapistCardProps> = ({
  id,
  name,
  title,
  yearsExperience,
  bio,
  imageUrl,
  className,
}) => {
  return (
    <Card
      hoverable
      padding="lg"
      className={cn('group flex h-full flex-col items-center text-center', className)}
    >
      {/* Avatar */}
      <div className="mb-4">
        <Avatar
          src={imageUrl}
          name={name}
          alt={`${name}, ${title}`}
          size="xl"
          ring
          ringColor="primary"
          className="h-24 w-24 transition-transform group-hover:scale-105"
        />
      </div>

      {/* Name */}
      <h3 className="mb-1 font-serif text-xl font-semibold text-gray-900 dark:text-slate-100">
        {name}
      </h3>

      {/* Title/Specialty */}
      <p className="mb-2 text-sm font-medium text-primary-600 dark:text-primary-400">{title}</p>

      {/* Years of Experience */}
      <p className="mb-3 text-sm text-gray-500 dark:text-slate-400">
        {yearsExperience} {yearsExperience === 1 ? 'year' : 'years'} of experience
      </p>

      {/* Bio */}
      <p className="mb-4 flex-grow text-gray-600 dark:text-slate-400">{bio}</p>

      {/* View Profile Link */}
      <Link
        to={`/therapists/${id}`}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 transition-colors hover:text-primary-700 dark:hover:text-primary-300"
      >
        View Profile
        <ArrowRight
          className="h-4 w-4 transition-transform group-hover:translate-x-1"
          aria-hidden="true"
        />
      </Link>
    </Card>
  );
};

TherapistCard.displayName = 'TherapistCard';
